import React from 'react'
import mermaid from 'mermaid'
import { useTheme } from '../context/ThemeContext'

let currentTheme: 'light' | 'dark' | null = null

function ensureInit(theme: 'light' | 'dark') {
  if (currentTheme === theme) return
  try {
    const css = getComputedStyle(document.documentElement)
    const panel = css.getPropertyValue('--panel').trim() || (theme === 'dark' ? '#101a2e' : '#ffffff')
    const text = css.getPropertyValue('--text').trim() || (theme === 'dark' ? '#d7e0ea' : '#0f172a')
    const border = css.getPropertyValue('--border').trim() || (theme === 'dark' ? '#22304a' : '#d6deea')
    const line = css.getPropertyValue('--link').trim() || (theme === 'dark' ? '#7bb0ff' : '#2a58d4')
    const panel2 = css.getPropertyValue('--panel-2').trim() || (theme === 'dark' ? '#122036' : '#f3f6fc')
    const codeBg = css.getPropertyValue('--code-bg').trim() || (theme === 'dark' ? '#0f2036' : '#f0f4fb')

    mermaid.initialize({
      startOnLoad: false,
      securityLevel: 'strict',
      theme: theme === 'dark' ? 'dark' : 'neutral',
      themeVariables: {
        background: 'transparent',
        primaryColor: panel2 || panel,
        primaryTextColor: text,
        primaryBorderColor: border,
        lineColor: line,
        secondaryColor: panel,
        tertiaryColor: codeBg,
      },
      fontFamily:
        'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
      deterministicIds: true,
    })
    currentTheme = theme
  } catch (_e) {
    // ignore; will retry on the next render
  }
}

export default function Mermaid({ chart, className }: { chart: string; className?: string }) {
  const [svg, setSvg] = React.useState<string>('')
  const [err, setErr] = React.useState<string>('')
  const renderId = React.useMemo(() => `mmd-${Math.random().toString(36).slice(2)}`, [chart])
  const { theme } = useTheme()

  React.useEffect(() => {
    let cancelled = false
    ensureInit(theme)
    setSvg('')
    setErr('')
    ;(async () => {
      try {
        const { svg } = await mermaid.render(renderId, chart)
        if (!cancelled) setSvg(svg)
      } catch (e: any) {
        if (!cancelled) setErr(e?.message || 'Failed to render mermaid diagram')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [chart, renderId, theme])

  if (err) {
    return (
      <div className="card" role="alert">
        Mermaid error: {err}
        <pre style={{ whiteSpace: 'pre-wrap' }}>{chart}</pre>
      </div>
    )
  }

  return (
    <div className={`mermaid-block ${className || ''}`.trim()} dangerouslySetInnerHTML={{ __html: svg }}/>
  )
}
